import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { UploadProvider } from './context/UploadContext';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import UpdateBanner from './components/UpdateBanner';
import UploadIndicator from './components/UploadIndicator';
import { isNative } from './native/backgroundMode';
import { getAuthToken, clearAuthToken } from './lib/tokenStorage';
import { registerForFcmPush } from './lib/pushFcm';
import ForceUpdateModal from './components/ForceUpdateModal';
import './App.css';

// Lazy-load all page components for route-based code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const BossDashboard = lazy(() => import('./pages/BossDashboard'));
const RecordSession = lazy(() => import('./pages/RecordSession'));
const ResearcherHome = lazy(() => import('./pages/ResearcherHome'));
const SessionDetail = lazy(() => import('./pages/SessionDetail'));
const ResearcherDetail = lazy(() => import('./pages/ResearcherDetail'));
const AllSessions = lazy(() => import('./pages/AllSessions'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BossChat = lazy(() => import('./pages/BossChat'));
const TeamFeed = lazy(() => import('./pages/TeamFeed'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AttendanceReport = lazy(() => import('./pages/AttendanceReport'));
const MyAttendance = lazy(() => import('./pages/MyAttendance'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MeetingRecorder = lazy(() => import('./pages/MeetingRecorder'));

// Page transition wrapper
const PageTransition = ({ children, authKey }) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionStage, setTransitionStage] = useState('page-fade-enter-active');

  useEffect(() => {
    setTransitionStage('page-fade-enter');
    const t = setTimeout(() => {
      setDisplayChildren(children);
      setTransitionStage('page-fade-enter-active');
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname, authKey]);

  useEffect(() => {
    setDisplayChildren(children);
  }, [authKey]);

  return <div className={transitionStage}>{displayChildren}</div>;
};

// Redirect /boss/researcher/:id → /admin/researcher/:id
const BossResearcherRedirect = () => {
  const { researcherId } = useParams();
  return <Navigate to={`/admin/researcher/${researcherId}`} replace />;
};

const AppRoutes = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('dark_mode') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try { localStorage.setItem('dark_mode', darkMode); } catch {};
  }, [darkMode]);

  // On mount, check if we have a stored token and fetch user profile
  useEffect(() => {
    const init = async () => {
      const token = await getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { default: axios } = await import('axios');
        const { API_BASE } = await import('./lib/api');
        const res = await axios.get(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
        registerForFcmPush().catch((e) => console.warn('FCM registration failed:', e));
      } catch (err) {
        console.error('[auth] Failed to fetch user profile:', err);
        if (err.response?.status === 401) {
          await clearAuthToken();
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    registerForFcmPush().catch((e) => console.warn('FCM registration failed:', e));
  };

  const handleLogout = async () => {
    await clearAuthToken();
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return <div className="min-h-[100dvh] flex items-center justify-center text-muted-foreground text-sm">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
      </div>
    </div>;
  }

  return (
    <UploadProvider>
    <a href="#main-content" className="skip-to-content">Skip to main content</a>
    <OfflineBanner />
    <UpdateBanner />
    <div id="main-content">
    <Suspense fallback={<div className="min-h-[100dvh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
    <PageTransition authKey={user?.id || 'guest'}>
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to={user.role === 'boss' ? '/admin' : '/'} /> : <LoginPage onLogin={handleLogin} />}
      />
      <Route
        path="/"
        element={
          !user ? <Navigate to="/login" /> :
          <ResearcherHome user={user} />
        }
      />
      <Route
        path="/record"
        element={
          !user ? <Navigate to="/login" /> :
          <RecordSession user={user} onLogout={handleLogout} />
        }
      />
      <Route
        path="/record/morning"
        element={
          !user ? <Navigate to="/login" /> :
          <RecordSession user={user} onLogout={handleLogout} sessionType="morning_plan" />
        }
      />
      <Route
        path="/record/evening"
        element={
          !user ? <Navigate to="/login" /> :
          <RecordSession user={user} onLogout={handleLogout} sessionType="evening_findings" />
        }
      />
      <Route
        path="/session/:sessionId"
        element={!user ? <Navigate to="/login" /> : <SessionDetail user={user} onLogout={handleLogout} />}
      />
      <Route
        path="/sessions"
        element={!user ? <Navigate to="/login" /> : user.role === 'boss' ? <Navigate to="/admin?tab=sessions" /> : <AllSessions user={user} />}
      />
      <Route
        path="/attendance"
        element={!user ? <Navigate to="/login" /> : <MyAttendance user={user} />}
      />
      <Route
        path="/boss"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <Navigate to="/admin" />
        }
      />
      <Route
        path="/admin/researcher/:researcherId"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <ResearcherDetail user={user} onLogout={handleLogout} />
        }
      />
      <Route
        path="/boss/researcher/:researcherId"
        element={<BossResearcherRedirect />}
      />
      <Route
        path="/admin/chat"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <BossChat user={user} onLogout={handleLogout} />
        }
      />
      <Route
        path="/boss/chat"
        element={<Navigate to="/admin/chat" />}
      />
      <Route
        path="/feed"
        element={!user ? <Navigate to="/login" /> : <TeamFeed user={user} />}
      />
      <Route
        path="/settings"
        element={!user ? <Navigate to="/login" /> : <SettingsPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} darkMode={darkMode} setDarkMode={setDarkMode} />}
      />
      <Route
        path="/boss/attendance"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <Navigate to="/admin?tab=attendance" />
        }
      />
      <Route
        path="/admin"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <AdminPage user={user} onLogout={handleLogout} />
        }
      />
      <Route
        path="/admin/meeting"
        element={
          !user ? <Navigate to="/login" /> :
          user.role !== 'boss' ? <Navigate to="/" /> :
          <MeetingRecorder user={user} onLogout={handleLogout} />
        }
      />
      <Route
        path="/meeting"
        element={<Navigate to="/admin/meeting" />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </PageTransition>
    </Suspense>
    </div>
    <UploadIndicator />
    </UploadProvider>
  );
};

const App = () => {
  const [forceUpdate, setForceUpdate] = useState(null);

  useEffect(() => {
    const handler = (e) => setForceUpdate(e.detail);
    window.addEventListener('force-apk-update', handler);
    return () => window.removeEventListener('force-apk-update', handler);
  }, []);

  return (
    <ErrorBoundary>
      <ForceUpdateModal
        show={forceUpdate !== null}
        currentBuild={forceUpdate?.current}
        latestBuild={forceUpdate?.latest}
      />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
